import type {
  SharedQuestionnaireAssignedClient,
  SharedQuestionnaireMedicalCondition,
} from '@/lib/api/questionnaires';
import {
  buildContextFromAnswers,
  isTriggerQuestion,
  parseGenderFromAnswer,
  parsePregnancyFromUnknown,
  questionLooksLikeMedicalTriggerContext,
  valuesSemanticallyMatch,
  type TriggerConditionContext,
} from '@/lib/questionnaire-triggers/evaluate';
import type { TriggerRuleConfig } from '@/lib/questionnaire-triggers/types';
import { defaultTriggerRules } from '@/lib/questionnaire-triggers/config';
import { OTHER_TEXT_SUFFIX, type JsonAnswerValue } from './QuestionRenderer';
import { isNoneLikeValue } from './noneOptions';
import type { JsonQuestion, JsonQuestionnaireSection, JsonQuestionOption } from './types';
import { getQuestionKey } from './questionKey';

export interface QuestionnaireDemographicContext {
  gender?: 'male' | 'female';
  age?: number;
}

function serializePrefillValue(value: JsonAnswerValue): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => String(item)).join('\u0001')}]`;
  }
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => `${key}:${String(item)}`)
      .join('\u0001');
  }
  return `${typeof value}:${String(value)}`;
}

export function buildQuestionnairePrefillScopeKey(
  sectionId: string | undefined,
  prefill: Record<string, JsonAnswerValue>
): string {
  const entries = Object.entries(prefill)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${serializePrefillValue(value)}`);
  return `${sectionId ?? ''}|${entries.join('\u0002')}`;
}

function normalizeBinarySex(
  value: string | undefined
): 'male' | 'female' | undefined {
  const v = value?.trim().toLowerCase();
  if (v === 'male' || v === 'female') return v;
  return undefined;
}

/** Effective male/female from assigned client (gender, then sexAtBirth). */
export function resolveGenderFromAssignedClient(
  client: SharedQuestionnaireAssignedClient | undefined
): 'male' | 'female' | undefined {
  if (!client) return undefined;
  const fromGender = normalizeBinarySex(client.gender);
  if (fromGender) return fromGender;
  return normalizeBinarySex(client.sexAtBirth);
}

function computeAgeFromDateOfBirth(dob: string): number | undefined {
  const match = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) {
    return undefined;
  }
  const now = new Date();
  let age = now.getUTCFullYear() - year;
  const m = now.getUTCMonth() - monthIndex;
  if (m < 0 || (m === 0 && now.getUTCDate() < day)) age -= 1;
  return age >= 0 ? age : undefined;
}

export function resolveAgeFromAssignedClient(
  client: SharedQuestionnaireAssignedClient | undefined
): number | undefined {
  if (!client) return undefined;
  if (client.dateOfBirth) {
    const fromDob = computeAgeFromDateOfBirth(client.dateOfBirth);
    if (fromDob != null) return fromDob;
  }
  const age = client.age;
  if (typeof age === 'number' && Number.isFinite(age) && age >= 0) return Math.trunc(age);
  return undefined;
}

/** Initial trigger fallback from API before / without in-form answers. */
export function triggerContextFromAssignedClient(
  client: SharedQuestionnaireAssignedClient | undefined
): TriggerConditionContext | undefined {
  if (!client) return undefined;
  const gender = resolveGenderFromAssignedClient(client);
  const age = resolveAgeFromAssignedClient(client);
  const pregnant =
    parsePregnancyFromUnknown(client.pregnancyStatus) ??
    parsePregnancyFromUnknown(client.pregnancyTrimester) ??
    parsePregnancyFromUnknown(client.pregnancyBreastfeedingStatus);
  return gender != null || age != null || pregnant != null ? { gender, age, pregnant } : undefined;
}

function questionTextBlob(question: JsonQuestion): string {
  return [
    question.questionText,
    question.externalId,
    question.label,
    question.targetMapping?.field,
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
}

export function isSexAtBirthQuestion(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  return (
    /sex assigned at birth|sex at birth|biological sex/.test(text) &&
    !/gender identity/.test(text)
  );
}

export function isGenderIdentityQuestion(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  if (isSexAtBirthQuestion(question)) return false;
  return /\bgender\b/.test(text);
}

function looksLikeDateOfBirthQuestion(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  if (question.type === 'date') {
    return /date of birth|dob|birth date|birthdate|birthday/.test(text);
  }
  return /\bdate of birth\b/.test(text) || /\bdob\b/.test(text) || /\bbirth date\b/.test(text);
}

function looksLikeBirthYearQuestion(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  return /\byear of birth\b/.test(text) || /\bbirth year\b/.test(text);
}

function isPregnancyQuestion(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  if (/\btwin\b|\bsingleton\b|\bpregnancy\s+type\b|\bis\s*twin\s*pregnancy\b/.test(text)) {
    return false;
  }
  return /pregnan|trimester|gestat/.test(text);
}

const PREGNANCY_RELATED_PREFILL_FIELDS = new Set([
  'breastfeedingstatus',
  'feedingtype',
  'istwinpregnancy',
  'lactationgoal',
  'lactationperiod',
  'pregnancybreastfeedingstatus',
  'pregnancystatus',
  'pregnancytrimester',
  'pregnancytype',
  'twinpregnancy',
]);

function normalizeProfileFieldName(field: string | undefined): string {
  return field?.replace(/[^a-z0-9]+/gi, '').toLowerCase() ?? '';
}

function isPregnancyRelatedPrefillQuestion(question: JsonQuestion, field: string | undefined): boolean {
  if (PREGNANCY_RELATED_PREFILL_FIELDS.has(normalizeProfileFieldName(field))) {
    return true;
  }

  const text = questionTextBlob(question);
  return /pregnan|trimester|gestat|lactat|breast\s*feed|breastfeed|feeding\s+type|\btwin\b|\bsingleton\b/.test(text);
}

function looksLikeAgeQuestion(question: JsonQuestion): boolean {
  if (looksLikeDateOfBirthQuestion(question) || looksLikeBirthYearQuestion(question)) return false;
  const text = questionTextBlob(question);
  return (
    text === 'age' ||
    /\bhow old are you\b/.test(text) ||
    /\bclient age\b/.test(text) ||
    /\bage in years\b/.test(text)
  );
}

function coerceAnswerForQuestion(
  question: JsonQuestion,
  raw: string | number | undefined
): JsonAnswerValue | undefined {
  if (raw == null) return undefined;
  const str = String(raw).trim();
  if (!str) return undefined;

  if (question.type === 'number') {
    const n = Number.parseFloat(str);
    return Number.isFinite(n) ? String(Math.trunc(n)) : str;
  }

  if (question.type === 'select' || question.type === 'radio' || question.type === 'multi-select') {
    const lower = str.toLowerCase().replace(/[_-]+/g, ' ');
    for (const opt of question.options ?? []) {
      const valueLower = opt.value.trim().toLowerCase().replace(/[_-]+/g, ' ');
      const labelLower = opt.label.trim().toLowerCase().replace(/[_-]+/g, ' ');
      if (valueLower === lower || labelLower === lower) {
        return question.type === 'multi-select' ? [opt.value] : opt.value;
      }
      if (
        (lower === 'non binary' || lower === 'nonbinary') &&
        (valueLower.includes('non') || labelLower.includes('non'))
      ) {
        return question.type === 'multi-select' ? [opt.value] : opt.value;
      }
    }
    return question.type === 'multi-select' ? [str] : str;
  }

  return str;
}

function getActiveQuestions(section: JsonQuestionnaireSection): JsonQuestion[] {
  return (section.questions ?? []).filter((q) => q.isActive !== false);
}

function collectAssignedClientConditionLabels(client: SharedQuestionnaireAssignedClient): string[] {
  const labels: string[] = [];
  for (const raw of client.medicalConditions ?? []) {
    if (typeof raw === 'string' && raw.trim()) labels.push(raw.trim());
  }
  return [...new Set(labels)];
}

function collectAssignedClientConditionSubtypeLabels(client: SharedQuestionnaireAssignedClient): string[] {
  const labels: string[] = [];
  for (const condition of client.medicalConditionDetails ?? []) {
    const subtype = condition.conditionSubtype?.trim();
    if (subtype && !isUnknownMedicalConditionSubtype(subtype)) labels.push(subtype);
  }
  return [...new Set(labels)];
}

function isUnknownMedicalConditionSubtype(subtype: string): boolean {
  return subtype
    .trim()
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() === 'not sure';
}

export function looksLikeMedicalConditionsQuestion(question: JsonQuestion): boolean {
  const isChoice =
    question.type === 'multi-select' || question.type === 'select' || question.type === 'radio';
  if (!isChoice) return false;

  const field = normalizeProfileFieldName(question.targetMapping?.field);
  if (field === 'medicalconditions' || field === 'preconsultationmedicalconditions') {
    return true;
  }

  const text = questionTextBlob(question);
  if (/\ballerg|\bintoler|\bdigestive\b|\bsymptoms?\b/.test(text)) return false;

  return (
    /\bmedical\s+conditions?\b/.test(text) ||
    /\bhealth\s+conditions?\b/.test(text) ||
    /\bdiagnosed\b.*\b(following|conditions?|health\s+conditions?|medical\s+conditions?)\b/.test(text)
  );
}

function matchOptionValuesForConditionLabels(
  question: JsonQuestion,
  conditionLabels: string[]
): string[] {
  const matched: string[] = [];
  for (const opt of question.options ?? []) {
    for (const label of conditionLabels) {
      if (
        valuesSemanticallyMatch(opt.value, label) ||
        valuesSemanticallyMatch(opt.label, label)
      ) {
        matched.push(opt.value);
        break;
      }
    }
  }
  return matched;
}

/** Condition labels that don't match any of this question's options (e.g. a free-text / non-standard condition). */
function findUnmatchedConditionLabels(question: JsonQuestion, conditionLabels: string[]): string[] {
  return conditionLabels.filter(
    (label) =>
      !(question.options ?? []).some(
        (opt) => valuesSemanticallyMatch(opt.value, label) || valuesSemanticallyMatch(opt.label, label)
      )
  );
}

function findOtherOption(question: JsonQuestion): JsonQuestionOption | undefined {
  return (question.options ?? []).find((opt) => {
    const value = opt.value.trim().toLowerCase();
    const label = opt.label.trim().toLowerCase();
    return value === 'other' || label === 'other';
  });
}

/**
 * Prefill diagnosis / medical-condition answers from the assigned client's profile
 * so conditional questionnaires can trigger without re-selecting conditions in the form.
 */
export function buildMedicalConditionsPrefill(
  section: JsonQuestionnaireSection,
  client: SharedQuestionnaireAssignedClient,
  triggerConfig: TriggerRuleConfig = defaultTriggerRules
): Record<string, JsonAnswerValue> {
  const prefill: Record<string, JsonAnswerValue> = {};
  const conditionLabels = collectAssignedClientConditionLabels(client);
  const conditionSubtypeLabels = collectAssignedClientConditionSubtypeLabels(client);
  if (conditionLabels.length === 0 && conditionSubtypeLabels.length === 0) return prefill;

  for (const question of getActiveQuestions(section)) {
    const isTriggerField =
      looksLikeMedicalConditionsQuestion(question) || isTriggerQuestion(question, triggerConfig);
    if (!isTriggerField) continue;

    const key = getQuestionKey(question);
    if (!key) continue;

    const labelsForQuestion = isTriggerQuestion(question, triggerConfig)
      ? [...conditionLabels, ...conditionSubtypeLabels]
      : conditionLabels;
    const matchedValues = matchOptionValuesForConditionLabels(question, labelsForQuestion);

    // A condition that doesn't match any of this question's options (e.g. a free-text / non-standard
    // condition like "Mental Health Condition") would otherwise be silently dropped from the prefill even
    // though it's an active condition on the client. If the question has an "Other" option, still surface
    // it there instead of hiding it.
    const otherOption = findOtherOption(question);
    const unmatchedLabels = otherOption ? findUnmatchedConditionLabels(question, conditionLabels) : [];
    if (matchedValues.length === 0 && unmatchedLabels.length === 0) continue;

    const selectedValues =
      unmatchedLabels.length > 0 && otherOption
        ? [...new Set([...matchedValues, otherOption.value])]
        : matchedValues;

    if (question.type === 'multi-select') {
      prefill[key] = selectedValues;
    } else if (question.type === 'select' || question.type === 'radio') {
      prefill[key] = selectedValues[0];
    }
    if (unmatchedLabels.length > 0) {
      prefill[`${key}${OTHER_TEXT_SUFFIX}`] = unmatchedLabels;
    }
  }

  // Follow-up type questions (e.g. "If Diabetes: which type?") from detailed labels.
  // Restrict to medical/condition-context questions; otherwise a generic "Other"
  // option (or a coincidental label match) gets prefilled on unrelated questions
  // such as occupation when a client has a free-text "other" medical condition.
  for (const question of getActiveQuestions(section)) {
    if (question.type !== 'select' && question.type !== 'radio') continue;
    if (!looksLikeMedicalConditionsQuestion(question) && !questionLooksLikeMedicalTriggerContext(question)) {
      continue;
    }
    const key = getQuestionKey(question);
    if (!key || prefill[key] !== undefined) continue;

    const matchedValues = matchOptionValuesForConditionLabels(question, [
      ...conditionLabels,
      ...conditionSubtypeLabels,
    ]);
    if (matchedValues.length > 0) {
      prefill[key] = matchedValues[0];
    }
  }

  return prefill;
}

/**
 * Demographics + medical-condition prefill for share link and manual fill.
 */
export function buildQuestionnairePrefillFromAssignedClient(
  section: JsonQuestionnaireSection,
  client: SharedQuestionnaireAssignedClient,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields,
  triggerConfig: TriggerRuleConfig = defaultTriggerRules
): Record<string, JsonAnswerValue> {
  return {
    ...buildAssignedClientPrefill(section, client, contextFields),
    ...buildAssignedClientProfileFieldPrefill(section, client),
    ...buildMedicalConditionsPrefill(section, client, triggerConfig),
  };
}

function isChoiceQuestion(question: JsonQuestion): boolean {
  return question.type === 'multi-select' || question.type === 'select' || question.type === 'radio';
}

function isYesNoPrompt(question: JsonQuestion): boolean {
  const text = questionTextBlob(question);
  return /\bdo you have\b|\bhave you\b/.test(text);
}

function getAssignedClientProfileValue(
  question: JsonQuestion,
  client: SharedQuestionnaireAssignedClient,
  field: string | undefined
): unknown {
  if (field === 'hasAllergies') return resolveSafetyToggleValue(client.hasAllergies, client.allergies);
  if (field === 'hasIntolerances') return resolveSafetyToggleValue(client.hasIntolerances, client.foodIntolerances);
  if (field === 'allergies') return meaningfulSafetySelections(client.allergies);
  if (field === 'foodIntolerances') return meaningfulSafetySelections(client.foodIntolerances);
  if (field === 'medicalConditions' || field === 'preConsultationMedicalConditions') return client.medicalConditions;

  const direct = field ? client[field as keyof SharedQuestionnaireAssignedClient] : undefined;
  if (direct !== undefined) return direct;

  if (!isChoiceQuestion(question)) return undefined;
  const text = questionTextBlob(question);
  if (/\ballerg/.test(text)) {
    return isYesNoPrompt(question)
      ? resolveSafetyToggleValue(client.hasAllergies, client.allergies)
      : meaningfulSafetySelections(client.allergies);
  }
  if (/\bintoler/.test(text)) {
    return isYesNoPrompt(question)
      ? resolveSafetyToggleValue(client.hasIntolerances, client.foodIntolerances)
      : meaningfulSafetySelections(client.foodIntolerances);
  }
  return undefined;
}

function meaningfulSafetySelections(values: string[] | null | undefined): string[] | undefined {
  if (!values?.length) return undefined;
  const meaningful = values.filter((value) => !isNoneLikeValue(value));
  return meaningful.length > 0 ? meaningful : undefined;
}

function inferSafetyToggleFromSelections(values: string[] | null | undefined): 'Yes' | 'No' | undefined {
  if (!values?.length) return undefined;
  return meaningfulSafetySelections(values)?.length ? 'Yes' : 'No';
}

function resolveSafetyToggleValue(
  explicitValue: string | null | undefined,
  selections: string[] | null | undefined
): string | undefined {
  const inferred = inferSafetyToggleFromSelections(selections);
  if (inferred === 'No') return 'No';
  return explicitValue ?? inferred;
}

function coerceAssignedClientProfileValue(
  question: JsonQuestion,
  value: unknown
): JsonAnswerValue {
  if (Array.isArray(value)) {
    if (question.type === 'multi-select') {
      const selected = value.flatMap((item) => {
        const coerced = coerceAnswerForQuestion(question, String(item));
        if (Array.isArray(coerced)) {
          return coerced.filter((coercedValue): coercedValue is string => typeof coercedValue === 'string');
        }
        return typeof coerced === 'string' ? [coerced] : [];
      });
      return selected.length > 0 ? selected : undefined;
    }
    return value.length > 0 ? coerceAnswerForQuestion(question, String(value[0])) : undefined;
  }

  if (typeof value === 'boolean') {
    return coerceAnswerForQuestion(question, value ? 'Twin' : 'Singleton');
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return coerceAnswerForQuestion(question, value);
  }

  return undefined;
}

function buildAssignedClientProfileFieldPrefill(
  section: JsonQuestionnaireSection,
  client: SharedQuestionnaireAssignedClient
): Record<string, JsonAnswerValue> {
  const prefill: Record<string, JsonAnswerValue> = {};

  for (const question of getActiveQuestions(section)) {
    const field = question.targetMapping?.field;
    const key = getQuestionKey(question);
    if (!key) continue;
    if (isPregnancyRelatedPrefillQuestion(question, field)) continue;

    const raw = getAssignedClientProfileValue(question, client, field);
    if (raw == null || raw === '') continue;

    const value = coerceAssignedClientProfileValue(question, raw);
    if (value !== undefined) prefill[key] = value;
  }

  return prefill;
}

/**
 * Prefill gender / sex-at-birth / age fields from assignedClient (link + manual fill).
 */
export function buildAssignedClientPrefill(
  section: JsonQuestionnaireSection,
  client: SharedQuestionnaireAssignedClient,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields
): Record<string, JsonAnswerValue> {
  const prefill: Record<string, JsonAnswerValue> = {};
  const genderRaw = client.gender?.trim();
  const sexAtBirthRaw = client.sexAtBirth?.trim();
  const genderIsBinary = normalizeBinarySex(genderRaw) != null;

  for (const question of getActiveQuestions(section)) {
    const key = getQuestionKey(question);
    if (!key) continue;

    if (isGenderIdentityQuestion(question) && genderRaw) {
      const value = coerceAnswerForQuestion(question, genderRaw);
      if (value !== undefined) prefill[key] = value;
      continue;
    }

    if (isSexAtBirthQuestion(question) && sexAtBirthRaw && !genderIsBinary) {
      const value = coerceAnswerForQuestion(question, sexAtBirthRaw);
      if (value !== undefined) prefill[key] = value;
      continue;
    }

    if (looksLikeDateOfBirthQuestion(question) && client.dateOfBirth) {
      const value = coerceAnswerForQuestion(question, client.dateOfBirth);
      if (value !== undefined) prefill[key] = value;
      continue;
    }

    if (looksLikeBirthYearQuestion(question) && client.dateOfBirth) {
      const birthYear = client.dateOfBirth.slice(0, 4);
      const value = coerceAnswerForQuestion(question, birthYear);
      if (value !== undefined) prefill[key] = value;
      continue;
    }

    if (looksLikeAgeQuestion(question)) {
      const age = resolveAgeFromAssignedClient(client);
      if (age != null) {
        const value = coerceAnswerForQuestion(question, age);
        if (value !== undefined) prefill[key] = value;
      }
      continue;
    }

    const aKeys = contextFields?.age;
    const ageKeyList = Array.isArray(aKeys) ? aKeys : aKeys ? [aKeys] : [];
    if (ageKeyList.some((k) => k === key || k === question.externalId || k === question._id)) {
      if (client.dateOfBirth) {
        const value = coerceAnswerForQuestion(question, client.dateOfBirth);
        if (value !== undefined) prefill[key] = value;
      } else {
        const age = resolveAgeFromAssignedClient(client);
        if (age != null) {
          const value = coerceAnswerForQuestion(question, age);
          if (value !== undefined) prefill[key] = value;
        }
      }
      continue;
    }

    const gKeys = contextFields?.gender;
    const keyList = Array.isArray(gKeys) ? gKeys : gKeys ? [gKeys] : [];
    if (keyList.some((k) => k === key || k === question.externalId || k === question._id)) {
      if (genderRaw) {
        const value = coerceAnswerForQuestion(question, genderRaw);
        if (value !== undefined) prefill[key] = value;
      } else if (sexAtBirthRaw && !genderIsBinary) {
        const value = coerceAnswerForQuestion(question, sexAtBirthRaw);
        if (value !== undefined) prefill[key] = value;
      }
    }
  }

  return prefill;
}

/**
 * Effective demographic context: in-form answers override assignedClient.
 */
function questionMatchesContextFieldKeys(
  question: JsonQuestion,
  keys: string | string[] | undefined
): boolean {
  if (!keys) return false;
  const list = Array.isArray(keys) ? keys : [keys];
  const key = getQuestionKey(question);
  const targetField = question.targetMapping?.field;
  return list.some(
    (k) => k === key || k === question.externalId || k === question._id || k === targetField
  );
}

/** Whether this questionnaire includes gender or sex-at-birth fields the user can answer. */
export function sectionHasGenderOrSexQuestions(
  section: JsonQuestionnaireSection | null,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields
): boolean {
  if (!section) return false;
  return getActiveQuestions(section).some(
    (q) =>
      isGenderIdentityQuestion(q) ||
      isSexAtBirthQuestion(q) ||
      questionMatchesContextFieldKeys(q, contextFields?.gender)
  );
}

/** Whether this questionnaire includes age / DOB fields the user can answer. */
export function sectionHasAgeQuestions(
  section: JsonQuestionnaireSection | null,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields
): boolean {
  if (!section) return false;
  return getActiveQuestions(section).some(
    (q) =>
      looksLikeDateOfBirthQuestion(q) ||
      looksLikeBirthYearQuestion(q) ||
      looksLikeAgeQuestion(q) ||
      questionMatchesContextFieldKeys(q, contextFields?.age)
  );
}

/** Answers for gender, sex-at-birth, and age fields only (avoids inferring from unrelated responses). */
function collectSectionDemographicAnswers(
  section: JsonQuestionnaireSection,
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields']
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  const assignAliases = (raw: unknown, keys: string | string[] | undefined) => {
    if (raw == null || keys == null) return;
    const list = Array.isArray(keys) ? keys : [keys];
    for (const alias of list) {
      if (!alias) continue;
      if (filtered[alias] !== undefined) continue;
      filtered[alias] = raw;
    }
  };

  for (const question of getActiveQuestions(section)) {
    const key = getQuestionKey(question);
    if (!key) continue;
    const isGender =
      isGenderIdentityQuestion(question) ||
      isSexAtBirthQuestion(question) ||
      questionMatchesContextFieldKeys(question, contextFields?.gender);
    const isAge =
      looksLikeDateOfBirthQuestion(question) ||
      looksLikeBirthYearQuestion(question) ||
      looksLikeAgeQuestion(question) ||
      questionMatchesContextFieldKeys(question, contextFields?.age);
    const isPregnancy =
      isPregnancyQuestion(question) ||
      questionMatchesContextFieldKeys(question, contextFields?.pregnancy);
    const isDemographic = isGender || isAge || isPregnancy;
    if (!isDemographic) continue;

    const raw = answers[key];
    if (raw === undefined) continue;

    filtered[key] = raw;
    if (question.externalId) filtered[question.externalId] = raw;
    if (question._id) filtered[question._id] = raw;
    if (question.targetMapping?.field) filtered[question.targetMapping.field] = raw;

    if (isGender) assignAliases(raw, contextFields?.gender);
    if (isAge) assignAliases(raw, contextFields?.age);
    if (isPregnancy) assignAliases(raw, contextFields?.pregnancy);
  }
  return filtered;
}

function augmentDemographicAnswerAliases(
  section: JsonQuestionnaireSection,
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields']
): Record<string, unknown> {
  const augmented: Record<string, unknown> = { ...answers };
  const assignAliases = (raw: unknown, keys: string | string[] | undefined) => {
    if (raw == null || keys == null) return;
    const list = Array.isArray(keys) ? keys : [keys];
    for (const alias of list) {
      if (!alias) continue;
      augmented[alias] = raw;
    }
  };

  for (const question of getActiveQuestions(section)) {
    const key = getQuestionKey(question);
    const raw = answers[key];
    if (raw == null) continue;
    const text = questionTextBlob(question);

    if (/(sex assigned at birth|biological sex|\bgender\b|\bsex\b)/.test(text)) {
      assignAliases(raw, contextFields?.gender);
    }
    if (
      looksLikeDateOfBirthQuestion(question) ||
      looksLikeBirthYearQuestion(question) ||
      looksLikeAgeQuestion(question) ||
      /\bhow old\b/.test(text) ||
      /\bage\b/.test(text) ||
      /\bdob\b/.test(text) ||
      /\bbirth\b/.test(text)
    ) {
      assignAliases(raw, contextFields?.age);
    }
    if (isPregnancyQuestion(question)) {
      assignAliases(raw, contextFields?.pregnancy);
    }
  }

  return augmented;
}

function pickGenderFromSectionQuestions(
  section: JsonQuestionnaireSection,
  answers: Record<string, unknown>,
  matchQuestion: (question: JsonQuestion) => boolean
): TriggerConditionContext['gender'] | undefined {
  let resolved: TriggerConditionContext['gender'] | undefined;
  for (const question of getActiveQuestions(section)) {
    if (!matchQuestion(question)) continue;
    const key = getQuestionKey(question);
    const parsed = parseGenderFromAnswer(question, answers[key]);
    if (parsed) resolved = parsed;
  }
  return resolved;
}

function pickGenderFromContextFieldKeys(
  section: JsonQuestionnaireSection,
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields']
): TriggerConditionContext['gender'] | undefined {
  const gKeys = contextFields?.gender;
  const keyList = Array.isArray(gKeys) ? gKeys : gKeys ? [gKeys] : [];
  if (keyList.length === 0) return undefined;

  let resolved: TriggerConditionContext['gender'] | undefined;
  for (const question of getActiveQuestions(section)) {
    const key = getQuestionKey(question);
    const matchesKey = keyList.some(
      (k) => k === key || k === question.externalId || k === question._id
    );
    if (!matchesKey) continue;
    const parsed = parseGenderFromAnswer(question, answers[key]);
    if (parsed) resolved = parsed;
  }
  return resolved;
}

/**
 * Resolve male/female for conditional questionnaire rules.
 * When sex-at-birth and gender identity disagree, prefer the value that diverged
 * from assignedClient (the user's edit) over stale API prefill on the other field.
 */
function resolveTriggerGenderFromSection(
  section: JsonQuestionnaireSection,
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields'],
  assignedClient: SharedQuestionnaireAssignedClient | undefined
): TriggerConditionContext['gender'] | undefined {
  const sexAtBirth = pickGenderFromSectionQuestions(section, answers, isSexAtBirthQuestion);
  const contextKeyGender = pickGenderFromContextFieldKeys(section, answers, contextFields);
  const genderIdentity = pickGenderFromSectionQuestions(section, answers, isGenderIdentityQuestion);
  const profileGender = resolveGenderFromAssignedClient(assignedClient);

  const primary = sexAtBirth ?? contextKeyGender;
  const secondary = genderIdentity;

  if (primary && secondary && primary !== secondary) {
    if (profileGender === primary && secondary !== profileGender) return secondary;
    if (profileGender === secondary && primary !== profileGender) return primary;
    return secondary;
  }

  return primary ?? secondary;
}

function resolveDemographicFromSectionAnswers(
  section: JsonQuestionnaireSection | null,
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields'],
  assignedClient?: SharedQuestionnaireAssignedClient
): TriggerConditionContext {
  if (!section) return {};
  const augmented = augmentDemographicAnswerAliases(section, answers, contextFields);
  const demographicOnly = collectSectionDemographicAnswers(section, augmented, contextFields);
  const fromAnswers = buildContextFromAnswers(demographicOnly, contextFields);

  const gender =
    resolveTriggerGenderFromSection(section, augmented, contextFields, assignedClient) ??
    fromAnswers.gender;

  return { gender, age: fromAnswers.age, pregnant: fromAnswers.pregnant };
}

export function questionMatchesSectionRef(
  questions: JsonQuestion[],
  questionId: string
): boolean {
  const normalized = questionId.trim();
  if (!normalized) return false;
  return questions.some((q) => {
    const key = getQuestionKey(q);
    return (
      key === normalized ||
      q._id === normalized ||
      q.questionId === normalized ||
      q.externalId === normalized
    );
  });
}

function selectedValuesFromAnswer(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'boolean') {
    return [value ? 'true' : 'false'];
  }
  if (typeof value === 'string' && value.trim().length > 0) return [value.trim()];
  return [];
}

function parseGenderOptionValue(value: string): 'male' | 'female' | undefined {
  const v = value.trim().toLowerCase();
  if (v === 'male' || v === 'm') return 'male';
  if (v === 'female' || v === 'f') return 'female';
  return normalizeBinarySex(v);
}

/**
 * When showWhen references a gender/sex question that is not in this questionnaire,
 * match option values against assignedClient-derived demographic context.
 */
export function showWhenConditionMatches(
  cond: { questionId: string; optionValues: string[] },
  answerForRefId: (questionId: string) => unknown,
  demographicContext: QuestionnaireDemographicContext | undefined,
  sectionQuestions: JsonQuestion[]
): boolean {
  const depAnswer = answerForRefId(cond.questionId);
  const selected = selectedValuesFromAnswer(depAnswer);
  const optionSet = new Set(cond.optionValues.map((v) => v.trim()));
  if (selected.some((s) => optionSet.has(s.trim()))) return true;

  if (questionMatchesSectionRef(sectionQuestions, cond.questionId)) return false;

  const effectiveGender = demographicContext?.gender;
  if (!effectiveGender) return false;

  return cond.optionValues.some((opt) => parseGenderOptionValue(opt) === effectiveGender);
}

/** Normalize client/API DOB strings to YYYY-MM-DD for prefill and age. */
export function normalizeProfileDateOfBirth(raw?: string | null): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString().slice(0, 10);
}

export type QuestionnaireClientProfileInput = {
  dateOfBirth?: string | null;
  gender?: string | null;
  sexAtBirth?: string | null;
  hasAllergies?: string | null;
  hasIntolerances?: string | null;
  allergies?: string[] | null;
  foodIntolerances?: string[] | null;
  medicalConditions?: string | null | string[];
  medicalConditionDetails?: Array<{
    conditionName?: string | null;
    displayName?: string | null;
    conditionId?: string;
    conditionSubtype?: string | null;
    name?: string;
  }>;
  activityLevel?: string | null;
  pregnancyBreastfeedingStatus?: string | null;
  pregnancyStatus?: string | null;
  pregnancyTrimester?: string | null;
  lactationPeriod?: string | null;
  lactationGoal?: string | null;
  isTwinPregnancy?: boolean | null;
  breastfeedingStatus?: string | null;
  feedingType?: string | null;
};

function mapProfileMedicalConditions(
  profile: QuestionnaireClientProfileInput
): Pick<SharedQuestionnaireAssignedClient, 'medicalConditions' | 'medicalConditionDetails'> {
  const medicalConditionDetails: SharedQuestionnaireMedicalCondition[] = (
    profile.medicalConditionDetails ?? []
  )
    .map((item) => ({
      name: item.conditionName ?? item.name ?? undefined,
      displayName: item.displayName ?? item.conditionName ?? item.name ?? undefined,
      conditionId: item.conditionId,
      conditionSubtype: item.conditionSubtype ?? undefined,
    }))
    .filter((item) => Boolean(item.name || item.displayName || item.conditionId));

  let medicalConditions: string[] | undefined;
  if (Array.isArray(profile.medicalConditions)) {
    medicalConditions = profile.medicalConditions.filter(
      (item): item is string => typeof item === 'string' && item.trim().length > 0
    );
  } else if (
    typeof profile.medicalConditions === 'string' &&
    profile.medicalConditions.trim().length > 0
  ) {
    medicalConditions = profile.medicalConditions
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return {
    medicalConditions: medicalConditions?.length ? medicalConditions : undefined,
    medicalConditionDetails: medicalConditionDetails.length ? medicalConditionDetails : undefined,
  };
}

/** Build share/manual-fill assignedClient from a client record (fills gaps when share API omits demographics). */
export function sharedAssignedClientFromClientProfile(
  profile: QuestionnaireClientProfileInput | null | undefined
): SharedQuestionnaireAssignedClient | undefined {
  if (!profile) return undefined;

  const dateOfBirth = normalizeProfileDateOfBirth(profile.dateOfBirth ?? undefined);
  const gender = typeof profile.gender === 'string' && profile.gender.trim() ? profile.gender.trim() : undefined;
  const sexAtBirth =
    typeof profile.sexAtBirth === 'string' && profile.sexAtBirth.trim()
      ? profile.sexAtBirth.trim()
      : undefined;
  const { medicalConditions, medicalConditionDetails } = mapProfileMedicalConditions(profile);
  const age = dateOfBirth ? computeAgeFromDateOfBirth(dateOfBirth) : undefined;

  if (
    !dateOfBirth &&
    !gender &&
    !sexAtBirth &&
    !age &&
    !medicalConditions?.length &&
    !medicalConditionDetails?.length &&
    !profile.allergies?.length &&
    !profile.foodIntolerances?.length &&
    !profile.activityLevel &&
    !profile.pregnancyStatus &&
    !profile.pregnancyTrimester &&
    !profile.lactationPeriod
  ) {
    return undefined;
  }

  return {
    dateOfBirth,
    gender,
    sexAtBirth,
    age,
    hasAllergies: profile.hasAllergies ?? null,
    hasIntolerances: profile.hasIntolerances ?? null,
    allergies: profile.allergies ?? undefined,
    foodIntolerances: profile.foodIntolerances ?? undefined,
    medicalConditions,
    medicalConditionDetails,
    activityLevel: profile.activityLevel ?? null,
    pregnancyBreastfeedingStatus: profile.pregnancyBreastfeedingStatus ?? null,
    pregnancyStatus: profile.pregnancyStatus ?? null,
    pregnancyTrimester: profile.pregnancyTrimester ?? null,
    lactationPeriod: profile.lactationPeriod ?? null,
    lactationGoal: profile.lactationGoal ?? null,
    isTwinPregnancy: profile.isTwinPregnancy ?? undefined,
    breastfeedingStatus: profile.breastfeedingStatus ?? null,
    feedingType: profile.feedingType ?? null,
  };
}

/** Prefer share API demographics; fall back to client profile for manual fill and prefill. */
export function mergeSharedQuestionnaireAssignedClient(
  apiClient?: SharedQuestionnaireAssignedClient,
  profileClient?: SharedQuestionnaireAssignedClient
): SharedQuestionnaireAssignedClient | undefined {
  if (!apiClient && !profileClient) return undefined;

  const medicalConditions = Array.isArray(apiClient?.medicalConditions)
    ? apiClient.medicalConditions
    : profileClient?.medicalConditions;
  const medicalConditionDetails = Array.isArray(apiClient?.medicalConditionDetails)
    ? apiClient.medicalConditionDetails
    : profileClient?.medicalConditionDetails;

  return {
    age: apiClient?.age ?? profileClient?.age,
    dateOfBirth: apiClient?.dateOfBirth ?? profileClient?.dateOfBirth,
    gender: apiClient?.gender ?? profileClient?.gender,
    sexAtBirth: apiClient?.sexAtBirth ?? profileClient?.sexAtBirth,
    hasAllergies: apiClient?.hasAllergies ?? profileClient?.hasAllergies,
    hasIntolerances: apiClient?.hasIntolerances ?? profileClient?.hasIntolerances,
    allergies: apiClient?.allergies?.length ? apiClient.allergies : profileClient?.allergies,
    foodIntolerances: apiClient?.foodIntolerances?.length ? apiClient.foodIntolerances : profileClient?.foodIntolerances,
    medicalConditions,
    medicalConditionDetails,
    activityLevel: apiClient?.activityLevel ?? profileClient?.activityLevel,
    pregnancyBreastfeedingStatus: apiClient?.pregnancyBreastfeedingStatus ?? profileClient?.pregnancyBreastfeedingStatus,
    pregnancyStatus: apiClient?.pregnancyStatus ?? profileClient?.pregnancyStatus,
    pregnancyTrimester: apiClient?.pregnancyTrimester ?? profileClient?.pregnancyTrimester,
    lactationPeriod: apiClient?.lactationPeriod ?? profileClient?.lactationPeriod,
    lactationGoal: apiClient?.lactationGoal ?? profileClient?.lactationGoal,
    isTwinPregnancy: apiClient?.isTwinPregnancy ?? profileClient?.isTwinPregnancy,
    breastfeedingStatus: apiClient?.breastfeedingStatus ?? profileClient?.breastfeedingStatus,
    feedingType: apiClient?.feedingType ?? profileClient?.feedingType,
  };
}

export function resolveQuestionnaireDemographicContext(
  answers: Record<string, unknown>,
  assignedClient: SharedQuestionnaireAssignedClient | undefined,
  section: JsonQuestionnaireSection | null,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields
): QuestionnaireDemographicContext {
  const derived = resolveDemographicFromSectionAnswers(
    section,
    answers,
    contextFields,
    assignedClient
  );
  const fromClient = triggerContextFromAssignedClient(assignedClient);

  const gender =
    derived.gender ??
    fromClient?.gender ??
    resolveGenderFromAssignedClient(assignedClient);
  const age =
    derived.age ?? fromClient?.age ?? resolveAgeFromAssignedClient(assignedClient);

  return { gender, age };
}

/**
 * Trigger context: gender / sex-at-birth / age from in-form answers when those questions
 * exist (live updates win); otherwise assignedClient from the share API.
 */
export function resolveEffectiveTriggerContext(
  answers: Record<string, unknown>,
  assignedClient: SharedQuestionnaireAssignedClient | undefined,
  section: JsonQuestionnaireSection | null,
  _triggerContext?: TriggerConditionContext,
  contextFields: TriggerRuleConfig['contextFields'] = defaultTriggerRules.contextFields
): TriggerConditionContext {
  const derived = resolveDemographicFromSectionAnswers(
    section,
    answers,
    contextFields,
    assignedClient
  );
  const fromClient = triggerContextFromAssignedClient(assignedClient);

  // In-form answers win; fall back to share/client profile when fields are empty.
  return {
    gender:
      derived.gender ??
      fromClient?.gender ??
      resolveGenderFromAssignedClient(assignedClient),
    age:
      derived.age ??
      fromClient?.age ??
      resolveAgeFromAssignedClient(assignedClient),
    pregnant: derived.pregnant ?? _triggerContext?.pregnant ?? fromClient?.pregnant,
  };
}

export function questionMatchesDemographic(
  question: JsonQuestion,
  context: QuestionnaireDemographicContext | undefined
): boolean {
  const restriction = question.gender;
  if (restriction && restriction !== 'all') {
    if (!context?.gender || context.gender !== restriction) return false;
  }

  if (context?.age != null) {
    if (typeof question.ageMin === 'number' && context.age < question.ageMin) return false;
    if (typeof question.ageMax === 'number' && context.age > question.ageMax) return false;
  }

  return true;
}
